import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Badge, Icon } from "common/components";
import {
  Warning,
  CheckCircle,
  TrendingUp,
  Build,
  Event,
  Article,
  GetApp,
  Refresh,
  ChevronRight,
  ArrowBack
} from "@mui/icons-material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { MainRoutes } from "common/config/route.config";
import {
  PredictiveAnalysis,
  IOTDataPoint,
  mockPredictiveAnalysis,
  mockIOTData
} from "../../data";
import "./PredictiveMaintenanceDashboard.scss";

const PredictiveMaintenanceDashboard: React.FC = () => {
  const navigate = useNavigate();

  const getStatusIcon = (status: PredictiveAnalysis["currentStatus"]) => {
    switch (status) {
      case "operational":
        return <CheckCircle className="status-icon status-operational" />;
      case "warning":
        return <Warning className="status-icon status-warning" />;
      case "critical":
        return <Warning className="status-icon status-critical" />;
    }
  };

  const getStatusClass = (status: PredictiveAnalysis["currentStatus"]) => {
    switch (status) {
      case "operational":
        return "status-operational";
      case "warning":
        return "status-warning";
      case "critical":
        return "status-critical";
    }
  };

  const getComplianceClass = (
    status: PredictiveAnalysis["complianceStatus"]
  ) => {
    switch (status) {
      case "compliant":
        return "compliance-compliant";
      case "warning":
        return "compliance-warning";
      case "non-compliant":
        return "compliance-non-compliant";
    }
  };

  const handleBack = () => {
    navigate(MainRoutes.DATA_INTEGRATION_HUB);
  };

  const handleRefresh = () => {
    // Implement refresh logic
  };

  const handleExport = () => {
    // Implement export logic
  };

  const criticalCount = mockPredictiveAnalysis.filter(
    (a) => a.currentStatus === "critical"
  ).length;
  const warningCount = mockPredictiveAnalysis.filter(
    (a) => a.currentStatus === "warning"
  ).length;
  const operationalCount = mockPredictiveAnalysis.filter(
    (a) => a.currentStatus === "operational"
  ).length;

  return (
    <>
      <HeaderNew showSearch={false} />

      <div className="predictive-maintenance-dashboard">
        <div className="dashboard-content">
          <HubHeader
            title="Data Integration Hub"
            description="Centralized platform for seamless data integration and workflow management."
            showBackButton={true}
            backRoute={`${MainRoutes.DATA_MAPPING_INTERFACE}?table=IOT_SENSOR_DATA&columns=TIMESTAMP&filters=%5B%5D`}
          />

          <div className="section-header">
            <div className="section-info">
              <h2>Predictive Maintenance Analysis</h2>
              <p>IoT Data augmented with IOGP Requirements</p>
            </div>
            <div className="action-buttons">
              <Button variant="secondary" onClick={handleRefresh}>
                <Refresh />
                Refresh
              </Button>
              <Button variant="secondary" onClick={handleExport}>
                <GetApp />
                Export Report
              </Button>
            </div>
          </div>
          <div className="dashboard-main-content">
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card critical">
                <div className="card-header">
                  <span className="card-label">Critical</span>
                  <Warning className="card-icon" />
                </div>
                <div className="card-value">{criticalCount}</div>
                <div className="card-description">
                  Immediate action required
                </div>
              </div>

              <div className="summary-card warning">
                <div className="card-header">
                  <span className="card-label">Warning</span>
                  <Warning className="card-icon" />
                </div>
                <div className="card-value">{warningCount}</div>
                <div className="card-description">Attention needed</div>
              </div>

              <div className="summary-card operational">
                <div className="card-header">
                  <span className="card-label">Operational</span>
                  <CheckCircle className="card-icon" />
                </div>
                <div className="card-value">{operationalCount}</div>
                <div className="card-description">Normal operation</div>
              </div>

              <div className="summary-card sensors">
                <div className="card-header">
                  <span className="card-label">IoT Sensors</span>
                  <TrendingUp className="card-icon" />
                </div>
                <div className="card-value">{mockIOTData.length}</div>
                <div className="card-description">Active monitoring</div>
              </div>
            </div>

            {/* Equipment Analysis */}
            <div className="equipment-analysis">
              {mockPredictiveAnalysis.map((analysis) => (
                <div
                  key={analysis.equipmentId}
                  className={`equipment-card ${getStatusClass(
                    analysis.currentStatus
                  )}`}
                >
                  <div className="equipment-header">
                    <div className="equipment-info">
                      <div className="equipment-title">
                        {getStatusIcon(analysis.currentStatus)}
                        <h3>{analysis.equipmentName}</h3>
                        <span className="equipment-badge">
                          {analysis.equipmentId}
                        </span>
                      </div>
                      <div className="equipment-details">
                        <span className="status-info">
                          <Build className="detail-icon" />
                          Status:{" "}
                          <span
                            className={`status-text ${getStatusClass(
                              analysis.currentStatus
                            )}`}
                          >
                            {analysis.currentStatus.toUpperCase()}
                          </span>
                        </span>
                        {analysis.predictedFailureDate && (
                          <span className="prediction-info">
                            <Event className="detail-icon" />
                            Predicted failure: {analysis.predictedFailureDate}
                          </span>
                        )}
                        <span
                          className={`compliance-info ${getComplianceClass(
                            analysis.complianceStatus
                          )}`}
                        >
                          <Article className="detail-icon" />
                          {analysis.complianceStatus
                            .replace("-", " ")
                            .toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="confidence-score">
                      <div className="score-label">Confidence Score</div>
                      <div className="score-value">{analysis.confidence}%</div>
                      <div className="score-bar">
                        <div
                          className="score-progress"
                          style={{ width: `${analysis.confidence}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {analysis.currentStatus !== "operational" && (
                    <div
                      className={`alert-section ${getStatusClass(
                        analysis.currentStatus
                      )}`}
                    >
                      <Warning className="alert-icon" />
                      <span className="alert-message">
                        {analysis.currentStatus === "critical"
                          ? "CRITICAL: Immediate intervention required"
                          : "WARNING: Equipment requires attention within 7 days"}
                      </span>
                    </div>
                  )}

                  <div className="equipment-content">
                    <div className="recommendations-section">
                      <h4 className="section-title">
                        <Build className="section-icon" />
                        Recommendations
                      </h4>
                      <ul className="recommendations-list">
                        {analysis.recommendations.map((rec, idx) => (
                          <li key={idx} className="recommendation-item">
                            <ChevronRight className="recommendation-icon" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="requirements-section">
                      <h4 className="section-title">
                        <Article className="section-icon" />
                        Related IOGP Requirements
                      </h4>
                      {analysis.relatedRequirements.length > 0 ? (
                        <div className="requirements-list">
                          {analysis.relatedRequirements.map((req) => (
                            <div key={req.id} className="requirement-item">
                              <div className="requirement-header">
                                <span className="requirement-id-badge">
                                  {req.id}
                                </span>
                                <span
                                  className={`priority-badge priority-${req.priority}`}
                                >
                                  {req.priority}
                                </span>
                              </div>
                              <div className="requirement-text">
                                {req.requirement}
                              </div>
                              {req.threshold && (
                                <div className="requirement-threshold">
                                  Threshold: {req.threshold}°C
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="no-requirements">
                          All parameters within normal range
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PredictiveMaintenanceDashboard;
